import {Searchable} from '@/services/searchEngine/fuzzySearch/Searchable';
import {PartnerType} from '@/modules/partner/enums/PartnerType';
import {Address} from '@/modules/multiForm/shared/MultiformModels';
import {addressFormat} from '@/filters/StringFilters';
import {PartnerCooperationItemType} from '@/modules/partner/enums/PartnerCooperationItemType';
import {Utils} from '@/services/utils/BasicUtils';
import {Person, PersonDto} from '@/models/Person';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';

export enum PartnerStatus {
  UNVERIFIED = 'Unverified',
  UNDER_DISCUSSION_INACTIVE = 'UnderDiscussionInactive',
  UNDER_DISCUSSION_ACTIVE = 'UnderDiscussionActive',
  CONTRACT_OFFERED_INACTIVE = 'ContractOfferedInactive',
  CONTRACT_OFFERED_ACTIVE = 'ContractOfferedActive',
  CONTRACT_SIGNED_INACTIVE = 'ContractSignedInactive',
  CONTRACT_SIGNED_ACTIVE = 'ContractSignedActive',
}

const displayStatusByType = (status: PartnerStatus): string => {
  return I18NGetter().usePartnerStatus[status];
}

export const generatePartnerStatusList = (): { name: string; value: PartnerStatus }[] => {
  return Object.keys(PartnerStatus).map((key) => ({
    name: displayStatusByType(PartnerStatus[key]),
    value: PartnerStatus[key],
  }));
};
export abstract class PartnerDTO {
  id!: number;
  name!: string;
  type?: PartnerType;
  globalPartner?: boolean;
  salesman: Array<Salesman> = [];
  sources: FinpackSource[] = [];
  requireReporting!: boolean;
  updatedAt!: string;
  addedAt!: string;
  owner?: PersonDto;
  status!: PartnerStatus;
}

export interface FinpackSource {
  id: number;
  name: string;
}

export abstract class PartnerCooperationItemDTO {
  active!: boolean;
  id?: number;
  name!: string;
  type?: PartnerCooperationItemType;
  quantity?: string;
}

export abstract class PartnerCooperationItemsUsersCount {
  allUsersQuantity!: number;
  assignedUsersQuantity!: number;
  collaboratorId?: number;
  partnerId!: number;
}

export class PartnerCooperationItem extends PartnerCooperationItemDTO {
  constructor(PartnerCooperationItemDTO?: PartnerCooperationItemDTO) {
    super();
    Utils.assign(this, PartnerCooperationItemDTO);
  }
}

export interface PartnerCooperationDirectory {
  name: string;
  disabled: boolean;
  type?: PartnerCooperationItemType | null;
  id?: number | null;
  organisationId?: number;
  active?: boolean;
}

export class CreatedPartnerModel {
  public id?: number;
  public name: Nullable<string> = null;
  public email: Nullable<string> = null;
  public nip: Nullable<string> = null;
  public regon?: Nullable<string> = null;
  public krs?: Nullable<string> = null;
  public globalPartner: boolean = false;
  public type!: PartnerType;
  public requireReporting: boolean = false;
  public expertFit: boolean = false;
  public primaryAddress: Address = new Address();
  public secondaryAddress: Address = new Address();
  public updatedAt: Nullable<string> = null
  public addedAt: Nullable<string> = null;
  public owner: Nullable<Person> = null;
  public ownerId?: Nullable<number> = null;
  public status: PartnerStatus = PartnerStatus.UNVERIFIED;

  public get displayAddress(): string {
    return addressFormat(this.primaryAddress);
  }

  public get displayStatus(): string {
    return displayStatusByType(this.status);
  }

  constructor(partner?: Partner) {
    Utils.assign(this, partner);
  }
}

export abstract class SalesmanDTO {
  id!: number;
  firstname!: string;
  lastname!: string;
}

export class Salesman extends SalesmanDTO {
  constructor(salesmanDTO?: SalesmanDTO) {
    super();
    Utils.assign(this, salesmanDTO);
  }

  get displayName(): string {
    return this.firstname + ' ' + this.lastname;
  }
}

export default class Partner extends PartnerDTO implements Searchable {
  public get textToSearch(): string {
    return this.name;
  }

  public get displayName(): string {
    return this.name;
  }

  public get displayStatus(): string {
    return displayStatusByType(this.status);
  }

  constructor(partnerDTO?: PartnerDTO) {
    super();
    if (partnerDTO && partnerDTO.salesman?.length > 0) {
      partnerDTO.salesman = partnerDTO.salesman.map(salesman => new Salesman(salesman));
    }
    if (partnerDTO && partnerDTO.owner) {
      partnerDTO.owner = new Person(partnerDTO.owner);
    }
    Utils.assign(this, partnerDTO);
  }
}

export const OWN_PAGES_PREFIX = 'Moje strony';
